<app-side-dialog
  #dialog
  [headerText]="isEdit ? 'Edit Resource Group' : 'New Resource Group'"
  [saveText]="isEdit ? 'Save' : 'Create'"
  [headerGlyph]="ResourceType.RESOURCE_GROUP | resourceGlyph"
  [busy]="loading"
  [body]="body"
  [validationFn]="validateForm.bind(this)"
  (save)="onSubmit()"
  [contentVisible]="!loading"
  [errorText]="
    this.isEdit ? 'Cannot edit resource group' : 'Cannot create resource group'
  "
  [errorMsg]="errorMsg"
></app-side-dialog>

<ng-template #body>
  <form [formGroup]="form" autocomplete="off" class="form-scroll">
    <app-constraint-notification
      [resource]="resource"
      [type]="constraintType"
    ></app-constraint-notification>
    <h3 class="section-title">Info</h3>
    <app-container
      [body]="basicInfoContainer"
      [isEnabled]="isNonSubresourceActionsEnabled"
      overlayTooltip="You do not have permission to edit these properties in the resource"
    ></app-container>

    <ng-template #basicInfoContainer>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <app-helpful-label
          [required]="true"
          for="resource-group-displayname-input"
          i18n-label="XFLD|Label of the Name input@@create.name.input.label"
          i18n-popoverTitle="
            XTIT|Title of the Name popover@@create.name.popover.title"
          label="Display Name"
          popoverTitle="Display Name"
          [popoverBody]="namePopover"
        >
        </app-helpful-label>
        <ng-template
          #namePopover
          i18n="XMSG|Body text of the Name popover@@create.name.popover.body"
        >
          This is the display name of your resource group. <br /><br />
          You can change this name after the resource group is created.
        </ng-template>
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="resource-group-displayname-input"
          placeholder="Enter a display name"
          formControlName="displayName"
          [disableControl]="!isNonSubresourceActionsEnabled"
          [state]="Utils.getValidationState(form.get('displayName'))"
          class="name-input dialog-form-input"
        />

        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("required")
        ) {
          <fd-form-message [type]="'error'">
            Display Name is required
          </fd-form-message>
        }
        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("invalidDisplayName")
        ) {
          <fd-form-message [type]="'error'">
            Name can be only alphabetical letters (a-z), numbers (0-9), and the
            following special characters: ":.,-_#~/()!?&#64;'\&$%[]+;".
          </fd-form-message>
        }
        @if (
          (form.get("displayName")!.touched ||
            form.get("displayName")!.dirty) &&
          form.get("displayName")!.getError("maxlength")
        ) {
          <fd-form-message [type]="'error'">
            Name must not be longer than 100 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <app-helpful-label
          [required]="true"
          for="resource-group-name-input"
          i18n-label="
            Input Label|Label of the Technical Name
            input@@create.technicalName.input.label"
          i18n-popoverTitle="
            XTIT|Title of the Technical Name
            popover@@create.technicalName.popover.title"
          label="Name"
          popoverTitle="Name"
          [popoverBody]="technicalNamePopover"
        >
        </app-helpful-label>
        <ng-template
          #technicalNamePopover
          i18n="
            XMSG|Body text of the Technical Name
            popover@@create.technicalName.popover.body"
        >
          The technical name of your resource group must:
          <ul>
            <li>
              Be DNS-compliant. It can contain only lowercase alphabetical
              characters (a-z), numeric characters (0-9), hyphens (-), and
              periods (.).
            </li>
            <li>Start and end with an alphanumeric character.</li>
            <li>
              Must be unique for all folders and resource groups in the current
              node in the structure.
            </li>
          </ul>
          Examples: my-resource-group, resourcegroup1 <br /><br />
          IMPORTANT: You cannot change the technical name once the resource
          group is created.
        </ng-template>
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="resource-group-name-input"
          placeholder="Enter a technical name"
          formControlName="name"
          [disableControl]="isEdit || !isNonSubresourceActionsEnabled"
          [state]="Utils.getValidationState(form.get('name'))"
          class="name-input dialog-form-input"
        />

        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("required")
        ) {
          <fd-form-message [type]="'error'">
            Technical Name is required
          </fd-form-message>
        }
        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("invalidName")
        ) {
          <fd-form-message [type]="'error'">
            Technical Name must be DNS-compliant. It can contain only lowercase
            alphabetical letters (a-z), numbers (0-9), hyphens (-), and periods
            (.). It must start and end with a lowercase letter or number.
          </fd-form-message>
        }
        @if (
          (form.get("name")!.touched || form.get("name")!.dirty) &&
          form.get("name")!.getError("maxlength")
        ) {
          <fd-form-message [type]="'error'">
            Technical name must not be longer than 63 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
      <fd-form-input-message-group [triggers]="['mouseenter', 'mouseleave']">
        <label fd-form-label for="description-input">Description</label>
        <input
          fd-form-control
          fdCompact
          type="text"
          name="name"
          id="description-input"
          placeholder="Enter a description"
          [state]="Utils.getValidationState(form.get('description'))"
          formControlName="description"
          [disableControl]="!isNonSubresourceActionsEnabled"
          class="name-input dialog-form-input"
        />
        @if (
          (form.get("description")!.touched ||
            form.get("description")!.dirty) &&
          form.get("description")!.getError("invalidDescription")
        ) {
          <fd-form-message [type]="'error'">
            Description can be only alphabetical letters (a-z), numbers (0-9),
            and the following special characters: ":.,-_#~/()!?&#64;'\&$%[]+;".
          </fd-form-message>
        }
        @if (
          (form.get("description")!.touched ||
            form.get("description")!.dirty) &&
          form.get("description")!.getError("maxlength")
        ) {
          <fd-form-message [type]="'error'">
            Description must not be longer than 250 characters.
          </fd-form-message>
        }
      </fd-form-input-message-group>
    </ng-template>

    <div class="separator"></div>
    <app-add-labels-and-annotations
      #addLabelsAndAnnotationsComponent
      [fixed]="true"
      [labels]="labels"
      [annotations]="annotations"
      [isLabelsEnabled]="isLabelsEnabled"
      [isAnnotationsEnabled]="isAnnotationEnabled"
    ></app-add-labels-and-annotations>
  </form>
</ng-template>
